/**
 * Kalshi Crypto Price Event Utilities
 *
 * Detects crypto price-level events (KXBTCD, KXETHD, KXSOLD) and provides
 * helpers for strike extraction, outcome filtering, and S3 data URLs.
 */

/** Map Kalshi series tickers to crypto symbols.
 *  Kalshi has multiple frequency variants per asset:
 *    KXBTCD (daily), KXBTC1H (hourly), KXBTC15M (15 min), KXBTCY (yearly), …
 *  Match the asset prefix; the underlying S3 candle data is the same.
 */
const SERIES_PREFIX_TO_SYMBOL: Array<[RegExp, string]> = [
  [/^KXBTC/, 'BTC'],
  [/^KXETH/, 'ETH'],
  [/^KXSOL/, 'SOL'],
];

/** Map crypto symbols to S3 file prefixes */
const SYMBOL_TO_S3: Record<string, string> = {
  BTC: 'btc',
  ETH: 'eth',
  SOL: 'sol',
};

// Matches "$66,900 or above" / "$66,900 or higher" (multi-strike daily) AND
// "Target Price: $68,255.68" (single-strike intraday like KXBTC15M).
const STRIKE_REGEX = /\$\s*([\d,]+(?:\.\d+)?)(?:\s*(?:or\s+)?(?:above|over|more|higher))?/i;

export interface CryptoEventInfo {
  isCrypto: true;
  symbol: string;        // 'BTC' | 'ETH' | 'SOL'
  s3Prefix: string;      // 'btc' | 'eth' | 'sol'
  seriesTicker: string;  // 'KXBTCD'
}

export type CryptoDetectionResult = CryptoEventInfo | { isCrypto: false };

/**
 * Detect if an event is a Kalshi crypto price-level event.
 * Uses seriesTicker as primary signal.
 */
export function detectCryptoEvent(seriesTicker: string | null | undefined): CryptoDetectionResult {
  if (!seriesTicker) return { isCrypto: false };
  const upper = seriesTicker.toUpperCase();
  for (const [re, symbol] of SERIES_PREFIX_TO_SYMBOL) {
    if (re.test(upper)) {
      return {
        isCrypto: true,
        symbol,
        s3Prefix: SYMBOL_TO_S3[symbol],
        seriesTicker: upper,
      };
    }
  }
  return { isCrypto: false };
}

/**
 * Extract strike price from a market title.
 * e.g. "$66,900 or above" → 66900
 */
export function extractStrike(title: string): number | null {
  const match = title.match(STRIKE_REGEX);
  if (!match) return null;
  return parseFloat(match[1].replace(/,/g, ''));
}

export interface StrikeMarket {
  index: number;
  strike: number;
}

/**
 * Extract strikes from all markets and return sorted by strike ascending.
 */
export function extractStrikes(markets: Array<{ groupItemTitle?: string | null; question?: string }>): StrikeMarket[] {
  const strikes: StrikeMarket[] = [];
  for (let i = 0; i < markets.length; i++) {
    const text = markets[i].groupItemTitle || markets[i].question || '';
    const strike = extractStrike(text);
    if (strike != null) {
      strikes.push({ index: i, strike });
    }
  }
  return strikes.sort((a, b) => a.strike - b.strike);
}

/**
 * Filter markets to the N nearest strikes around a current price.
 * Returns indices of the markets to display (in strike order).
 */
export function filterNearestStrikes(
  strikes: StrikeMarket[],
  currentPrice: number,
  count: number = 5,
): number[] {
  if (strikes.length <= count) return strikes.map(s => s.index);

  // Find the strike closest to current price
  let closestIdx = 0;
  let minDist = Infinity;
  for (let i = 0; i < strikes.length; i++) {
    const dist = Math.abs(strikes[i].strike - currentPrice);
    if (dist < minDist) {
      minDist = dist;
      closestIdx = i;
    }
  }

  // Take count strikes centered around the closest
  const half = Math.floor(count / 2);
  let start = Math.max(0, closestIdx - half);
  let end = start + count;
  if (end > strikes.length) {
    end = strikes.length;
    start = Math.max(0, end - count);
  }

  return strikes.slice(start, end).map(s => s.index);
}

/**
 * Compute an "implied current spot" from the markets themselves — the
 * strike whose YES price is closest to 50¢ is, by definition, the
 * market's view of the current underlying. Used as a fallback for
 * price-level events where we don't have an external spot feed
 * (WTI oil, indices, commodities — anything that isn't BTC/ETH/SOL).
 *
 * Returns null when no strike has a meaningful YES price (entire book
 * dead) so callers can decide not to filter.
 */
export function computeImpliedSpotFromStrikes(
  strikes: StrikeMarket[],
  getYes: (marketIndex: number) => number | null | undefined,
): number | null {
  if (strikes.length === 0) return null;
  let best: { strike: number; dist: number } | null = null;
  for (const s of strikes) {
    const yes = getYes(s.index);
    if (yes == null || Number.isNaN(yes)) continue;
    // Skip strikes pegged to 0 or 1 — they're resolved-ish and would
    // never beat a contested mid-strike on the distance check, but
    // including them when no contested strike exists would put us on
    // the far tail of the book.
    if (yes <= 0.005 || yes >= 0.995) continue;
    const dist = Math.abs(yes - 0.5);
    if (!best || dist < best.dist) best = { strike: s.strike, dist };
  }
  return best?.strike ?? null;
}

/**
 * Find which strike contains the current price.
 * Returns the strike index where price >= strike and price < next strike.
 */
export function findCurrentStrike(strikes: StrikeMarket[], currentPrice: number): number | null {
  if (strikes.length === 0) return null;
  // Find the highest strike that is <= currentPrice
  for (let i = strikes.length - 1; i >= 0; i--) {
    if (strikes[i].strike <= currentPrice) return strikes[i].index;
  }
  return null;
}

/**
 * Parse the resolution/target time from a Kalshi crypto event ticker.
 * Format: KXBTCD-26APR0306 → YY=26, MMM=APR, DD=03, HH=06
 * Returns ISO date string in EDT (UTC-4).
 */
const TICKER_MONTHS: Record<string, number> = {
  JAN: 0, FEB: 1, MAR: 2, APR: 3, MAY: 4, JUN: 5,
  JUL: 6, AUG: 7, SEP: 8, OCT: 9, NOV: 10, DEC: 11,
};

export function parseTickerTargetTime(eventTicker: string): string | null {
  // Match patterns:
  //   KXBTCD-26APR0306         (daily/hourly: yy mmm dd hh)
  //   KXBTC15M-26APR071015     (15-minute: yy mmm dd hh mm)
  const match = eventTicker.match(/-(\d{2})([A-Z]{3})(\d{2})(\d{2})(\d{2})?$/);
  if (!match) return null;
  const [, yy, mmm, dd, hh, mm] = match;
  const month = TICKER_MONTHS[mmm];
  if (month == null) return null;
  const year = 2000 + parseInt(yy);
  const day = parseInt(dd);
  const hour = parseInt(hh);
  const minute = mm ? parseInt(mm) : 0;
  // EDT = UTC-4. Convert EDT hour to UTC.
  const utcHour = hour + 4;
  const date = new Date(Date.UTC(year, month, day, utcHour, minute, 0));
  return date.toISOString();
}
